import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from './OverlayPanel';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Input, Button } from './design-system';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';

export interface EntityPickerItem {
  id: string;
  label: string;
  subtitle?: string;
}

export interface EntityPickerModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void;
  title: string;
  confirmText?: string;
  allowMultiple?: boolean;
  /** Fetches one page. Return hasMore=false once there's nothing left. */
  fetchItems: (opts: { search: string; skip: number; limit: number }) => Promise<{ items: EntityPickerItem[]; hasMore: boolean }>;
}

const PAGE_SIZE = 20;

/** Generic search-select modal for picking one or more existing records —
 * built for Vendor/Catalogue picking (Products' "Add to Vendor"/"Add to
 * Category" bulk actions), where no dedicated selector component existed
 * yet (unlike Products, which already has ProductSelectorModal). */
export const EntityPickerModal: React.FC<EntityPickerModalProps> = ({
  visible,
  onClose,
  onConfirm,
  title,
  confirmText = 'Confirm',
  allowMultiple = false,
  fetchItems,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const [items, setItems] = useState<EntityPickerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const load = useCallback(async (nextPage: number, reset: boolean) => {
    try {
      if (reset) setLoading(true);
      const res = await fetchItems({ search: searchQuery, skip: nextPage * PAGE_SIZE, limit: PAGE_SIZE });
      setItems((prev) => (reset ? res.items : [...prev, ...res.items]));
      setHasMore(res.hasMore);
      setPage(nextPage);
    } catch {
      // leave whatever list is already showing — a failed page fetch isn't
      // worth a full-screen error for a picker modal.
    } finally {
      setLoading(false);
    }
  }, [fetchItems, searchQuery]);

  useEffect(() => {
    if (!visible) return;
    setSelectedIds(new Set());
    setSearchQuery('');
    load(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const timeout = setTimeout(() => load(0, true), 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selectedIds.size === 0) return;
    onConfirm(Array.from(selectedIds));
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.headerTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{title}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchWrapper}>
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              leftIcon={<Ionicons name="search" size={18} color={colors.text.secondary} />}
            />
          </View>

          {loading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ padding: spacing.lg }} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={(item) => item.id}
              style={styles.list}
              ListEmptyComponent={<Text style={styles.emptyText}>No results found</Text>}
              onEndReached={() => hasMore && load(page + 1, false)}
              onEndReachedThreshold={0.4}
              renderItem={({ item }) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <TouchableOpacity
                    style={[styles.row, { flexDirection: rtl.rowDirection }, isSelected && styles.rowActive]}
                    onPress={() => toggle(item.id)}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.rowLabel, isSelected && styles.rowLabelActive]} numberOfLines={1}>{item.label}</Text>
                      {item.subtitle ? (
                        <Text style={[styles.rowSubtitle, isSelected && styles.rowLabelActive]} numberOfLines={1}>{item.subtitle}</Text>
                      ) : null}
                    </View>
                    <Ionicons
                      name={isSelected ? 'checkmark-circle' : allowMultiple ? 'ellipse-outline' : 'radio-button-off'}
                      size={20}
                      color={isSelected ? colors.primary : colors.text.disabled}
                    />
                  </TouchableOpacity>
                );
              }}
            />
          )}

          <View style={styles.footer}>
            <Button
              title={selectedIds.size > 0 ? `${confirmText} (${selectedIds.size})` : confirmText}
              onPress={handleConfirm}
              disabled={selectedIds.size === 0}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: 'flex-end',
    // Desktop: a centered card like search and the filter panels; phones keep the bottom sheet.
    ...(Platform.OS === 'web' && { justifyContent: 'center', padding: spacing.lg }),
  },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: spacing.borderRadius.lg,
    borderTopRightRadius: spacing.borderRadius.lg,
    maxHeight: '80%',
    minHeight: 360,
    ...(Platform.OS === 'web' && { maxWidth: 480, alignSelf: 'center', width: '100%', ...overlayCardLook(), overflow: 'hidden' }),
  },
  header: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  searchWrapper: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
  },
  list: {
    paddingHorizontal: spacing.lg,
    marginTop: spacing.sm,
  },
  row: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.borderRadius.md,
  },
  rowActive: {
    backgroundColor: colors.background.surfaceRaised,
  },
  rowLabel: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontWeight: '500',
  },
  rowLabelActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  rowSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled,
    textAlign: 'center',
    paddingVertical: spacing.xl,
  },
  footer: {
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
});
